import React, {useCallback, useMemo} from 'react';
import {createSelector} from 'reselect';

import {useMemoizedSelector} from '../../state/hooks';
import * as ViewHooks from '../../state/views/hooks';
import * as RunSetActions from '../../state/views/runSet/actions';
import type {Ref as RunSetRef} from '../../state/views/runSet/types';
import {observePerfWindow} from '../../util/datadog/criticalWorkflows/perfWindow/observer';
import type {PerfWindowInitiatingEvent} from '../../util/datadog/criticalWorkflows/perfWindow/types';
import * as Query from '../../util/queryts';
import type {GroupSelectionState} from '../../util/selectionmanager';
import {TailwindContents} from '../../weave/components/Tailwind';
import {useAddToHistory} from '../PanelBank/search/useAddToSearchHistory';
import {type RunSearchHistoryItem} from '../PanelBank/types';
import {type Project, ProjectPicker} from '../ProjectPicker';
import {RunsSearchBar} from '../RunsSearchBar';
import {useRunSelectorFeatureEnabled} from './RunSelectorFeaturesContext';
import {useRunSelectorProjectInfo} from './RunSelectorRunSetContext';
import {makeRunSetSelector} from './runSetSelectors';
import {type WBTableComp} from './WBTable/WBTable';

type RunSelectorExtraTableActionsTop = {
  enableSetProject: boolean;
  groupSelections: GroupSelectionState;
  runSetRef: RunSetRef;
  setProject: (
    newEntityName: string,
    newProjectName: string,
    newProjectId: string
  ) => void;
  tableRef: React.RefObject<WBTableComp | null>;
  searchErrorMessage?: string;
};

const makeRunsSearchSelector = (runSetRef: RunSetRef) =>
  createSelector(makeRunSetSelector(runSetRef), runSet => ({
    search: runSet.search,
    searchHistory: runSet.searchHistory,
  }));

export function RunSelectorExtraTableActionsTop(
  props: RunSelectorExtraTableActionsTop
) {
  'use memo';
  const isEnabled = useRunSelectorFeatureEnabled();
  const {projectName, entityName, projectInternalId} =
    useRunSelectorProjectInfo();

  const partialRunSet = useMemoizedSelector(
    makeRunsSearchSelector,
    props.runSetRef
  );

  const runSetUpdate = ViewHooks.useViewAction(
    props.runSetRef,
    RunSetActions.update
  );

  const setSearch = useCallback(
    (
      query: string,
      isRegex: boolean,
      initiatingEvent?: PerfWindowInitiatingEvent
    ) => {
      observePerfWindow({
        trigger: 'workflow',
        workflow: 'run-search',
        windowDurationMs: 4_000,
        ...(initiatingEvent == null ? {} : {initiatingEvent}),
        workflowContext: {
          queryLength: query.length,
          searchMode: isRegex ? 'regex' : 'text',
        },
      });
      if (props.tableRef.current) {
        props.tableRef.current.resetPagination();
      }
      runSetUpdate({search: {query, isRegex}});
    },
    [props.tableRef, runSetUpdate]
  );

  const setSearchHistory = useCallback(
    (searchHistory: RunSearchHistoryItem[] | undefined) =>
      runSetUpdate({searchHistory}),
    [runSetUpdate]
  );

  const addToHistory = useAddToHistory(
    setSearchHistory,
    partialRunSet.searchHistory
  );

  const search = useMemo(
    () => ({
      query: partialRunSet.search.query,
      isRegex: partialRunSet.search.isRegex ?? true,
    }),
    [partialRunSet.search.query, partialRunSet.search.isRegex]
  );

  const handleSelectProject = (project: Project | null) => {
    if (!project) {
      return;
    }
    props.setProject(project.entityName, project.name, project.internalId);
  };
  const selectedProject = {
    internalId: projectInternalId,
    name: projectName,
    entityName: entityName,
  } as Project;

  return (
    <TailwindContents>
      {props.enableSetProject && projectInternalId && (
        <div className="w-[250px]">
          <ProjectPicker
            aria-label="Projects"
            entityName={entityName}
            fullWidth
            onSelectProject={handleSelectProject}
            selectedProject={selectedProject}
          />
        </div>
      )}
      {isEnabled('search') ? (
        <div style={{flexGrow: 1}}>
          <RunsSearchBar
            isGroupSearch={Query.isGroupedByGroup(
              props.groupSelections.grouping
            )}
            search={search}
            setSearch={setSearch}
            searchHistory={partialRunSet.searchHistory}
            onAddToHistory={addToHistory}
            errorMessage={props.searchErrorMessage}
          />
        </div>
      ) : null}
    </TailwindContents>
  );
}
